import {
  getNextStep,
  validateStep,
  visibleFields,
  visibleSteps,
} from "./runtime";
import type {
  QuestionnaireAnswers,
  QuestionnaireDefinition,
  QuestionnaireStep,
} from "./schema";

export type QuestionnaireFeedbackScope = "form" | "section";

/** Guidance about effort and the actual path, never an assessment of an answer. */
export function questionnaireFeedback(input: {
  definition: QuestionnaireDefinition;
  answers: QuestionnaireAnswers;
  stepId: string;
  scope?: QuestionnaireFeedbackScope;
  hasIntroduction?: boolean;
}): string | null {
  const { definition, answers, stepId, scope = "section" } = input;
  const path: QuestionnaireStep[] = [];
  const visible = visibleSteps(definition, answers);
  let step: QuestionnaireStep | undefined =
    visible.find((item) => item.id === definition.initialStepId) ?? visible[0];
  // Use the runtime's routing rules, including skipped steps. Never guess on a cycle.
  while (step) {
    if (path.some((item) => item.id === step?.id)) return null;
    path.push(step);
    step = getNextStep(definition, step.id, answers) ?? undefined;
  }
  const questions = path.filter((item) =>
    visibleFields(item, answers).some(
      (field) => field.kind !== "page" && field.kind !== "computed_display",
    ),
  );
  const index = questions.findIndex((item) => item.id === stepId);
  const current = questions[index];
  if (!current || questions.length < 2) return null;
  if (index === 0)
    return input.hasIntroduction ? null : "Let’s take it one step at a time.";
  const prior = questions.slice(0, index);
  if (!prior.every((item) => validateStep({ step: item, answers }).success))
    return null;

  // An answer on this screen could reveal another question or change the route.
  // Wait until the next screen before making any promise about the ending.
  const bindings = new Set(current.fields.map((field) => field.binding));
  const dependsOnCurrent = (value: unknown): boolean => {
    if (!value || typeof value !== "object") return false;
    if (
      "field" in value &&
      typeof value.field === "string" &&
      bindings.has(value.field)
    )
      return true;
    return Object.values(value).some(dependsOnCurrent);
  };
  const canChangePath = definition.steps.some(
    (item) =>
      dependsOnCurrent(item.visibleWhen) ||
      dependsOnCurrent(item.transitions) ||
      item.fields.some((field) => dependsOnCurrent(field.visibleWhen)),
  );
  // Receipts don't request more input. Reviews and consent still count as steps.
  const beforeReceipt = path.filter(
    (item) =>
      !item.fields.every(
        (field) => field.kind === "page" && field.placement === "end",
      ),
  );
  const last =
    beforeReceipt[beforeReceipt.length - 1]?.id === stepId && !canChangePath;
  if (last) {
    const fields = visibleFields(current, answers);
    const contactOnly = fields.every(
      (field) =>
        field.kind === "phone" ||
        /^contact\.(fullName|name|email|phone|firstName|lastName)$/.test(
          field.source?.reference ?? "",
        ) ||
        /(^|\.)(fullName|name|email|phone|firstName|lastName)$/.test(
          field.binding,
        ),
    );
    if (scope === "section")
      return contactOnly
        ? "Just your contact details to wrap up this part."
        : "You’re at the last step of this section.";
    return contactOnly
      ? "Almost there. Just your contact details left."
      : "One last step, then you’re all set.";
  }
  // One acknowledgement in the middle, preferably at a section boundary.
  const boundary = questions.findIndex(
    (item, i) => i > 1 && item.stage && item.stage !== questions[i - 1]?.stage,
  );
  const milestone =
    boundary >= 0
      ? boundary
      : questions.length >= 5
        ? Math.floor(questions.length / 2)
        : -1;
  return index === milestone ? "Thanks for sharing those details." : null;
}
